import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface Team {
  id: string;
  name: string;
  seed?: number;
}

interface MatchData {
  id: string;
  team1: Team | null;
  team2: Team | null;
  team1_score: number | null;
  team2_score: number | null;
  round: number;
  match_number: number;
  winner_id: string | null;
}

const ROUND_NAMES: Record<number, string> = {
  1: 'Round of 16',
  2: 'Quarter Finals',
  3: 'Semi Finals',
  4: 'Finals'
};

const TournamentBracket: React.FC = () => {
  const [matches, setMatches] = useState<MatchData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Group matches by round
  const matchesByRound = matches.reduce<Record<number, MatchData[]>>((acc, match) => {
    if (!acc[match.round]) {
      acc[match.round] = [];
    }
    acc[match.round].push(match);
    return acc;
  }, {});

  // Sort rounds in ascending order
  const sortedRounds = Object.keys(matchesByRound)
    .map(Number)
    .sort((a, b) => a - b);

  // Fetch matches
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const { data: matchesData, error } = await supabase
          .from('tournament_matches')
          .select(`
            *,
            team1:team1_id (id, name),
            team2:team2_id (id, name)
          `)
          .order('round', { ascending: true })
          .order('match_number', { ascending: true });

        if (error) throw error;

        setMatches(matchesData || []);
      } catch (error) {
        console.error('Error fetching matches:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatches();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading tournament bracket...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center text-red-500">Tournament Bracket</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {sortedRounds.map((round) => (
            <div key={round} className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="bg-red-600 px-4 py-3">
                <h2 className="text-lg font-semibold text-center">{ROUND_NAMES[round] || `Round ${round}`}</h2>
              </div>
              <div className="p-4 space-y-4">
                {matchesByRound[round]?.map((match) => (
                  <div 
                    key={match.id}
                    className={`p-3 rounded-md ${
                      match.winner_id 
                        ? 'bg-gray-700 border-l-4 border-red-500' 
                        : 'bg-gray-800 border-l-4 border-gray-600'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className={`${match.winner_id === match.team1?.id ? 'text-red-400 font-medium' : 'text-gray-300'}`}>
                        {match.team1?.name || 'TBD'}
                      </div>
                      <div className="text-gray-400">
                        {match.team1_score !== null ? match.team1_score : '-'}
                      </div>
                    </div>
                    <div className="h-px bg-gray-600 my-2"></div>
                    <div className="flex justify-between items-center">
                      <div className={`${match.winner_id === match.team2?.id ? 'text-red-400 font-medium' : 'text-gray-300'}`}>
                        {match.team2?.name || 'TBD'}
                      </div>
                      <div className="text-gray-400">
                        {match.team2_score !== null ? match.team2_score : '-'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TournamentBracket;
